import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Ref } from 'vue';
import type { Event, Theme } from '@/resources/types';
import { PRODUCT_GIFTCARD_TYPE, } from '@/resources/helpers/constants';
import { differenceInDays, formatDate } from '@/resources/helpers';

import uniq from 'lodash/uniq';

interface FetchProductOptions {
  page: string | null;
  perPage: string | null;
  typeIds: string[] | null;
  themeUuid: string[] | null;
  themeCategoryUuid: string[] | null;
  hostUuid: string[] | null;
  venueUuid: string[] | null;
  taxonId?: number | string | null;
  areSalesStopped: boolean;
  codes: String[];
  dates: String[] | null;
  skills: String[] | null;
}

export const useProductStore = defineStore('product', () => {
  const productsLoading: Ref<boolean> = ref(false);
  const products: Ref<Event[]> = ref([]);
  const productFetchOptions: Ref<FetchProductOptions | null> = ref(null);
  const productsPagination: Ref<any | null> = ref(null);
  const product: Ref<Event | null> = ref(null);
  const productLoading: Ref<boolean> = ref(true);
  const theme: Ref<Theme | null> = ref(null);
  const themeLoading: Ref<boolean> = ref(false);
  const onlineEvents: Ref<Array<any>> = ref([]);
  const offlineEvents: Ref<Array<any>> = ref([]);
  const giftCardsProducts: Ref<Array<any>> = ref([]);
  const resetingProductLoad: Ref<boolean> = ref(true);
  const asyncMotivesLinks: Ref<Array<any>> = ref([]);
  const MAX_THEMES = 10;

  async function fetchProducts(options: Partial<FetchProductOptions>, returnData: boolean = false, isLoadMore: boolean = false, endpoint: string = '/api/tyk/events') {
    if (JSON.stringify(productFetchOptions.value) === JSON.stringify(options) && !isLoadMore) {
      if (returnData) {
        return products.value;
      }
      return;
    }

    try {
      productsLoading.value = true;

      const { result, code } = await $fetch(endpoint, {
        query: options
      });

      if (returnData) {
        return result?.events || [];
      }
      if (isLoadMore) {
        products.value.push(...result?.events)
      } else {
        products.value = result?.events
      }

      productsPagination.value = result?.pagination || null;
      productFetchOptions.value = options as FetchProductOptions;

      giftCardsProducts.value = products.value?.filter(product => product.product_type === PRODUCT_GIFTCARD_TYPE)
      onlineEvents.value = products.value?.filter(product => !product.cityName &&
        product.__typename === "Event")
      offlineEvents.value = products.value?.filter(product => product.cityName &&
        product.__typename === "Event")
    } catch (error) {
      console.error(error);
    } finally {
      productsLoading.value = false;
    }
  }

  function resetProduct() {
    product.value = null;
    productLoading.value = true;
    resetingProductLoad.value = false

  }
  function resetProducts() {
    products.value = [];
    productsPagination.value = null;
    productFetchOptions.value = null;
    resetingProductLoad.value = false

  }

  async function fetchProduct(code: string, returnData: boolean = false, isPhysical: boolean = false) {
    productLoading.value = true;

    const checkIfProductExists = products.value.find((product) => product.slug === code);
    if (checkIfProductExists) {
      product.value = checkIfProductExists;
      productLoading.value = false;
      return;
    }

    const { result } = await $fetch(`/api/tyk/${isPhysical ? 'physical-products' : 'event'}?code=${code}${isPhysical ? '&slug' : '&hidden=0&enabled=1'}`);
    if (Array.isArray(result?.events) && result?.events?.length > 0) {
      if (returnData) {
        productLoading.value = false;
        return result.events[0];
      }

      product.value = result.events[0];
    }
    productLoading.value = false;
    return product.value;
  }

  async function fetchTheme(id: string) {
    try {
      themeLoading.value = true;
      const response = await $fetch(`/api/tyk/theme?id=${id}`);
      theme.value = response || null;
      themeLoading.value = false;
    } catch (error) {
      console.error(error);
      throw new Error('Failed to fetch theme');
    }
  }

  function canLoadMoreProducts(page: number): boolean {
    if (productsPagination.value) {
      return !productsLoading.value && (Math.ceil(productsPagination.value.total / productsPagination.value.per_page)) > page;
    }

    return false;
  }

  async function fetchAsyncMotivesProduct(motives: any[]) {
    asyncMotivesLinks.value = await Promise.all(
      motives.map(async (item) => {
        const result = await fetchProducts(
          {
            page: '1',
            perPage: '1',
            themeUuid: item.themeUuid,
            taxonId: 23,
            mainTaxonCodes: 'online_events'
          },
          true
        );
        return { ...item, link: `/events/${result?.[0]?.slug}` };
      })
    );
  }

  return {
    product,
    productLoading,
    products,
    productsLoading,
    productsPagination,
    theme,
    themeLoading,
    fetchTheme,
    fetchProducts,
    fetchProduct,
    resetProducts,
    resetProduct,
    canLoadMoreProducts,
    onlineEvents,
    offlineEvents,
    giftCardsProducts,
    resetingProductLoad,
    fetchAsyncMotivesProduct,
    asyncMotivesLinks
  };
});
